import { Stack, Typography } from '@mui/material';
import { LinkedinTypes } from 'enums';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import PaiActionText from '../../ui/base-components/pai-action-text';
import PaiButton from '../../ui/base-components/pai-button';
import PaiDropzone from '../../ui/base-components/pai-dropzone';
import PaiHelpIcon from '../../ui/base-components/pai-help-icon';
import PaiLink from '../../ui/base-components/pai-link';
import PaiRadioItem from '../../ui/base-components/pai-radio-item';
import { SearchTypes } from './linkedin-profile-finder.types';

interface Props {
  linkedinProduct: LinkedinTypes;
  searchType: SearchTypes;
  keywords: string[];

  handleLinkedinProductChange(value: string): () => void;

  handleSearchTypeChange(value: string): () => void;

  removeKeywords(): void;

  onCsvDrop(acceptedFiles: any): void;

  handleOpenProjectNameDialog(): void;
}

const ProvideContactInformation: React.FC<Props> = ({
  linkedinProduct,
  searchType,
  keywords,
  handleLinkedinProductChange,
  handleSearchTypeChange,
  removeKeywords,
  onCsvDrop,
  handleOpenProjectNameDialog,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const exampleFileContacts = '/dashboard/examples/url generation example contacts.csv';
  const exampleFileWebsite = '/dashboard/examples/url generation example website visitors.csv';

  const linkedinProductOptions = [
    { label: 'LinkedIn', value: LinkedinTypes.LINKEDIN },
    { label: 'LinkedIn + Sales Navigator', value: LinkedinTypes.SALES_NAV },
  ];

  const searchTypeOptions = [
    { label: t('nameAndCompany'), value: SearchTypes.CONTACTS },
    { label: t('companyAndRole'), value: SearchTypes.WEBSITE },
  ];

  return (
    <Stack className={classes.provideContactInformation} gap={3}>
      <div className={classes.action}>
        <PaiActionText text={t('back')} backArrow={true} action={() => navigate(-1)} />
      </div>
      <Stack gap={2}>
        <Stack direction={'row'} gap={1}>
          <Typography className={classes.subTitle}>{t('whichLinkedinProduct')}</Typography>
          <PaiHelpIcon tooltipText={t('selectLinkedinProduct')} />
        </Stack>
        <Stack direction={'column'} gap={1}>
          {linkedinProductOptions.map((option) => (
            <PaiRadioItem
              key={option.value}
              label={option.label}
              value={option.value}
              selectedValue={linkedinProduct}
              variant="row"
              handleSelectItem={handleLinkedinProductChange}
            />
          ))}
        </Stack>
      </Stack>
      <Stack gap={2}>
        <Stack direction={'row'} gap={1}>
          <Typography className={classes.subTitle}>{t('profileFinderSearchTypes')}</Typography>
          <PaiHelpIcon tooltipText={t('profileFinderSearchTypesDescription')} />
        </Stack>
        <Stack direction={'column'} gap={1}>
          {searchTypeOptions.map((option) => (
            <PaiRadioItem
              key={option.value}
              label={option.label}
              value={option.value}
              selectedValue={searchType}
              variant="row"
              handleSelectItem={handleSearchTypeChange}
            />
          ))}
        </Stack>
      </Stack>
      <Stack gap={2}>
        <Stack direction={'row'} gap={1}>
          <Typography className={classes.subTitle}>{t('searchLinkedinUrlsViaCsv')}</Typography>
          <PaiHelpIcon tooltipText={t('addContactsViaFileInstruction')} />
        </Stack>
        <Stack direction={'row'} gap={4}>
          <Stack gap={2} alignItems={'center'} sx={{ width: '100%', maxWidth: '600px' }}>
            <PaiDropzone onDrop={onCsvDrop} />
            {!_.isEmpty(keywords) ? (
              <Stack gap={1} alignItems={'center'} justifyContent={'center'}>
                <Typography className={classes.result}>{t('rowsCount', { count: keywords.length })}</Typography>
                <PaiLink text={t('remove')} size={'md'} variant={'primary'} underline={'always'} action={removeKeywords} />
              </Stack>
            ) : (
              <div>
                <PaiLink
                  text={t('downloadExample')}
                  size={'md'}
                  variant={'primary'}
                  underline={'hover'}
                  href={searchType === SearchTypes.CONTACTS ? exampleFileContacts : searchType === SearchTypes.WEBSITE ? exampleFileWebsite : ''}
                  download={true}
                />
              </div>
            )}
          </Stack>
        </Stack>
      </Stack>

      <Stack direction={'row'} justifyContent={'flex-end'} sx={{ marginTop: 'auto' }}>
        <PaiButton text={t('continue')} action={handleOpenProjectNameDialog} disabled={_.isEmpty(keywords)} />
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  provideContactInformation: {
    position: 'relative',
    width: '100%',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    padding: '20px 16px',
    height: 'calc(100% - 16px - 20px)',
  },
  action: {
    position: 'absolute',
    left: 0,
    top: '-38px',
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
  result: {
    fontSize: theme.typography.pxToRem(13),
  },
}));

export default ProvideContactInformation;
